import { ElementType, useContext, useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { styled } from '@mui/material/styles';
import { ConversationDrawer, Messages, UserInput } from '@/components/conversation';
import { ConversationContext } from '@my-helpers/common';
import Threads from '@/components/conversation/Threads';
import { LinearProgress, Tooltip } from '@mui/material';
import { MessageView } from '@my-helpers/common';

const ConversationContainer = styled(Box)(({ theme }) => ({
	flexGrow: 1,
	padding: 0,
	display: 'flex',
	flexDirection: 'column',
	transition: theme.transitions.create(['margin', 'width'], {
		easing: theme.transitions.easing.sharp,
		duration: theme.transitions.duration.enteringScreen,
	}),
	minHeight: 0 // Important for flex child to be scrollable
}));

export const ConversationView: ElementType = () => {
	const ctx = useContext(ConversationContext);
	if (!ctx) {
			throw new Error('ConversationView must be used within a ConversationProvider');
		}
		const {
			isLoading,
			error,
			currentConversation,
			availableHelpers } = ctx.view;
		const {
			refreshMessage,
			startConversation,
			submitMessage,
			getResponses,
			selectThread,
			clearError
		} = ctx.command;

		const [selectedParticipant, setSelectedParticipant] = useState<string | undefined>();
		const [expandedThinkBlocks, setExpandedThinkBlocks] = useState<Set<string>>(new Set());
		const [contextUsage, setContextUsage] = useState<number>(0);
		const [messages, setMessages] = useState<MessageView[]>([]);

		useEffect(() => {
			if (!currentConversation) {
				return;
			}
			setMessages(currentConversation.currentThread.messages);
			setSelectedParticipant(currentConversation.participant_id);
			setContextUsage(currentConversation.currentThread.context_usage);
		}, [currentConversation]);
		
		return (
			<ConversationContainer>
				<Box
					display="flex"
					flex={1}
					flexDirection="row"
					padding={0}
					margin={0}
					overflow="hidden">
					<Box
						display="flex"
						flex={1}
						flexDirection="column"
						padding={0}
						margin={0}
						overflow="hidden">
						<Threads
							selectThread={selectThread}
							currentConversation={currentConversation}
						/>
						<Tooltip title={`${contextUsage.toFixed(1)}%`} arrow placement="top">
							<LinearProgress
								variant="determinate"
								value={contextUsage} />
						</Tooltip>
						<Messages
							isLoading={isLoading}
							error={error}
							messages={messages}
							expandedThinkBlocks={expandedThinkBlocks}
							setExpandedThinkBlocks={setExpandedThinkBlocks}
							getResponses={(msgId: string) => getResponses(
								currentConversation?.id ?? '', 
								currentConversation?.currentThread?.id ?? '', 
								msgId
							)}
							refreshMessage={(msgId: string) => refreshMessage(
								currentConversation?.id ?? '', 
								currentConversation?.currentThread?.id ?? '', 
								msgId)}
						/>
					</Box>
					<ConversationDrawer
						isLoading={isLoading}
						currentConversation={currentConversation}
						availableHelpers={availableHelpers}
						selectedParticipant={selectedParticipant}
						setSelectedParticipant={setSelectedParticipant}
					/>
				</Box>
				<UserInput
					isLoading={isLoading}
					messageStatus={messages[messages.length - 1]?.status}
					currentConversation={currentConversation}
					selectedParticipant={selectedParticipant}
					startConversation={startConversation}
					submitMessage={submitMessage}
					setExpandedThinkBlocks={setExpandedThinkBlocks}
				/>
			</ConversationContainer>
		);
	};

